import styled, { css } from 'styled-components';
import { Popout as PopoutComponent, Input as InputComponent, LinkButton } from 'src/components';

export const Content = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.lg};
    justify-content: center;
    padding-top: ${theme.sz.s6};
    margin: auto;
  `,
);

export const Header = styled.div(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.l};
    @media (max-width: ${theme.sz.md}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const CurrentLanguageContainer = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s8};
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      'flag language arrow'
      'flag hint arrow';
    column-gap: ${theme.sz.s2};
    align-items: center;

    @media (max-width: ${theme.sz.md}) {
      margin-top: ${theme.sz.s5};
      grid-template-areas:
        'flag language arrow'
        'hint hint hint';
      row-gap: ${theme.sz.s2};
    }
  `,
);

export const CurrentLanguageFlag = styled.div(
  ({ theme }) => css`
    grid-area: flag;
    height: ${theme.sz.s12};
    width: ${theme.sz.s12};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);

export const CurrentLanguageFlagImage = styled.img(
  ({ theme }) => css`
    height: ${theme.sz.s10};
    width: ${theme.sz.s10};
    background-color: ${theme.co.neutral60};
    border-radius: ${theme.bo.round};
  `,
);

export const CurrentLanguageName = styled.div(
  ({ theme }) => css`
    grid-area: language;
    ${theme.typography.heading.desktop.xs};
  `,
);

export const CurrentLanguageHint = styled.div(
  ({ theme }) => css`
    grid-area: hint;
    color: ${theme.co.secondary40};
    ${theme.typography.heading.desktop.xxxs};
  `,
);

export const ChangeLanguageButton = styled(LinkButton)(
  ({ theme }) => css`
    grid-area: arrow;
    font-size: ${theme.sz.s5};
  `,
);

export const SecondaryHeader = styled.div(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
    margin-top: ${theme.sz.s8};
    margin-bottom: ${theme.sz.s4};

    @media (max-width: ${theme.sz.md}) {
      margin-top: ${theme.sz.s5};
    }
  `,
);

export const SettingsBlock = styled.div(
  ({ theme }) => css`
    border: ${theme.bo.thinWidth} solid ${theme.co.neutral95};
    border-radius: ${theme.sz.s3};
  `,
);

export const SettingsRowWrapper = styled.div(
  ({ theme }) => css`
    padding: ${theme.sz.s2} ${theme.sz.s2} ${theme.sz.s2} ${theme.sz.s6};

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s2};
    }
  `,
);

export const Divider = styled.div(
  ({ theme }) => css`
    border-bottom: ${theme.bo.thinWidth} solid ${theme.co.neutral95};
  `,
);

export const LoaderContainer = styled.div(
  ({ theme }) => css`
    margin: ${theme.sz.s40} auto 0 auto;
    width: ${theme.sz.s40};
    @media (max-width: ${theme.sz.xl}) {
      width: ${theme.sz.s12};
    }
  `,
);

export const SwitchRow = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-areas:
      'title switch'
      'description switch';
    grid-template-columns: 1fr max-content;
    padding: ${theme.sz.s6};
    gap: ${theme.sz.s1} ${theme.sz.s6};

    @media (max-width: ${theme.sz.md}) {
      grid-template-areas:
        'title switch'
        'description description';
    }
  `,
);

export const SwitchRowTitle = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    display: flex;
    grid-area: title;
  `,
);
export const SwitchRowDescription = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
    grid-area: description;
  `,
);
export const SwitchRowSwitch = styled.div(
  () => css`
    grid-area: switch;
    display: flex;
    align-items: center;
  `,
);

export const InfoButton = styled.button(
  ({ theme }) => css`
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    margin-left: ${theme.sz.s2};
    font-size: ${theme.sz.s4};
    cursor: pointer;
  `,
);

export const ModalContent = styled.div(
  ({ theme }) => css`
    padding: ${theme.sz.s5};
  `,
);

export const ModalHeader = styled.div(
  ({ theme }) => css`
    font-size: ${theme.DEPRECATED_ty.large};
    font-weight: ${theme.DEPRECATED_ty.bold};
    margin-right: ${theme.sz.s12};
  `,
);

export const ImageWrapper = styled.div(
  ({ theme }) => css`
    width: 100%;
    margin-top: ${theme.sz.s5};
    padding: ${theme.sz.s6} ${theme.sz.s2};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral95};
  `,
);

export const Image = styled.div<{ $src: string }>(
  ({ theme, $src }) => css`
    background-image: url(${$src});
    background-position: top;
    background-size: contain;
    background-repeat: no-repeat;
    min-height: ${theme.sz.s54};

    @media (max-width: ${theme.sz.md}) {
      min-height: ${theme.sz.m};
    }
  `,
);

export const StreakWrapper = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr auto;
    justify-items: stretch;
    gap: ${theme.sz.s5};

    @media (max-width: ${theme.sz.sm}) {
      grid-template-columns: 1fr;
      gap: ${theme.sz.s4};
    }
  `,
);

export const StreakButtonWrapper = styled.div(
  ({ theme }) => css`
    min-width: fit-content;

    button {
      width: 100%;
    }
    @media (max-width: ${theme.sz.lg}) {
      width: 100%;
    }
  `,
);

export const Input = styled(InputComponent)(
  ({ theme }) => css`
    margin: 0;
    border-width: ${theme.bo.thinWidth};
    border-color: ${theme.co.secondary90};
    padding: ${theme.sz.s3} ${theme.sz.s4};
  `,
);

export const Popout = styled(PopoutComponent)(
  () => css`
    width: max-content;
  `,
);

export const LanguageActionHeader = styled(SecondaryHeader)(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s05};
  `,
);

export const LanguageActionRedHeader = styled(LanguageActionHeader)(
  ({ theme }) => css`
    color: ${theme.co.error40};
  `,
);

export const LanguageActionRow = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.sz.s4};

    > *:last-child {
      flex-shrink: 0;
    }

    @media (max-width: ${theme.sz.md}) {
      display: grid;
      grid-template-columns: 1fr;
    }
  `,
);

export const LanguageActionDescription = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
  `,
);
